import {
  emailAddress,
  money,
  orderId,
  productId,
  quantity,
  userId,
  type Order,
  type OrderItem,
  type PaymentMethod,
  type PlaceOrderResult,
  type PostalAddress,
  type ShippingOption,
  type CheckoutPaymentOptions,
} from '@sshop/domain';

import type {
  CheckoutPaymentOptionsResponseDto,
  OrderDto,
  PlaceOrderResponseDto,
  ShippingOptionDto,
} from './order.dto';

function toAddress(dto: OrderDto['shipping_address']): PostalAddress {
  return {
    recipientName: dto.recipient_name,
    phone: dto.phone,
    country: dto.country,
    city: dto.city,
    street: dto.street,
    apartment: dto.apartment,
    postalCode: dto.postal_code,
    notes: dto.notes,
    latitude: dto.latitude,
    longitude: dto.longitude,
  };
}

function toItem(dto: OrderDto['items'][number]): OrderItem {
  return {
    productId: productId(dto.product_id),
    title: dto.title,
    unitPrice: money(dto.unit_price.amount, dto.unit_price.currency),
    quantity: quantity(dto.quantity),
    lineTotal: money(dto.line_total.amount, dto.line_total.currency),
  };
}

function toPaymentMethod(dto: OrderDto['payment_method']): PaymentMethod {
  return {
    kind: dto.kind,
    displayName: dto.display_name,
    lastFour: dto.last_four,
  };
}

export function toOrder(dto: OrderDto): Order {
  return {
    id: orderId(dto.id),
    number: dto.number,
    userId: dto.user_id !== null ? userId(dto.user_id) : null,
    customerEmail: emailAddress(dto.customer_email),
    status: dto.status,
    items: dto.items.map(toItem),
    totals: {
      subtotal: money(dto.totals.subtotal.amount, dto.totals.subtotal.currency),
      discount: money(dto.totals.discount.amount, dto.totals.discount.currency),
      shipping: money(dto.totals.shipping.amount, dto.totals.shipping.currency),
      tax: money(dto.totals.tax.amount, dto.totals.tax.currency),
      total: money(dto.totals.total.amount, dto.totals.total.currency),
    },
    promotions: [],
    shippingAddress: toAddress(dto.shipping_address),
    billingAddress: dto.billing_address ? toAddress(dto.billing_address) : null,
    paymentMethod: toPaymentMethod(dto.payment_method),
    placedAt: dto.placed_at,
    updatedAt: dto.updated_at,
  };
}

export function toPlaceOrderResult(dto: PlaceOrderResponseDto): PlaceOrderResult {
  return {
    order: toOrder(dto.order),
    redirectUrl: dto.redirect_url,
    statusToken: dto.status_token,
  };
}

export function toShippingOption(dto: ShippingOptionDto): ShippingOption {
  return {
    id: dto.id,
    label: dto.label,
    price: money(dto.price.amount, dto.price.currency),
    freeShippingThreshold: dto.free_shipping_threshold ?? null,
    cashAllowed: dto.cash_allowed ?? false,
    noticeEnabled: dto.notice_enabled ?? false,
    region: dto.region ?? null,
  };
}

export function toCheckoutPaymentOptions(
  dto: CheckoutPaymentOptionsResponseDto,
): CheckoutPaymentOptions {
  return {
    bogSdkUrl: dto.data.bog_sdk_url,
    contact: dto.data.contact,
    savedShipping: dto.data.saved_shipping
      ? {
          cityId: dto.data.saved_shipping.city_id,
          address: dto.data.saved_shipping.address,
          notes: dto.data.saved_shipping.notes,
          phone: dto.data.saved_shipping.phone,
          latitude: dto.data.saved_shipping.latitude,
          longitude: dto.data.saved_shipping.longitude,
        }
      : null,
    savedPaymentMethods: dto.data.saved_payment_methods.map((method) => ({
      id: method.id,
      provider: method.provider,
      cardType: method.card_type,
      maskedCard: method.masked_card,
    })),
  };
}
